/** @format */

import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ArticleIcon from "@mui/icons-material/Article";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import moment from "moment";
import { ATTACHMENT_FIELDS } from "../../utils/constants";
import { isKeyUploadAllowed } from "./cf-v2-submitted-inputs";

const getFileIcon = (accept = "") => {
  const a = accept.toLowerCase();
  if (a.includes("pdf")) return { Icon: PictureAsPdfIcon, color: "#d93025" };
  if (a.includes("xls")) return { Icon: GridOnIcon, color: "#0f9d58" };
  if (a.includes("ppt")) return { Icon: SlideshowIcon, color: "#d24726" };
  if (a.includes("doc")) return { Icon: ArticleIcon, color: "#1a73e8" };
  return { Icon: InsertDriveFileIcon, color: "#5f6368" };
};

const getAttachmentUploadedAt = (stepHistory, key, fallback) => {
  if (key !== "unitEconomics") {
    return "";
  }
  if (!Array.isArray(stepHistory)) return fallback;
  const updates = stepHistory
    .filter((s) => s.attachments && s.attachments[key])
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return updates[0]?.timestamp || fallback;
};

/**
 * "Attachments" accordion (expanded by default).
 * Renders the CF form's document attachments as cards, with view + re-upload actions.
 * Upload state/handlers live in the parent (they touch shared form state) and are
 * passed in as `uploadingKey`, `onReupload`, `fileInputRef`, and `onFileChange`.
 *
 * `canUpload` is the step/user-level gate (upload-enabled step AND admin/authorised approver).
 * Whether a *specific* attachment can be re-uploaded is then decided per key against the
 * step's `editableFilesObject` (a key locked to `false` stays read-only) — mirroring the
 * backend's `isStepUploadEnabled` + `isKeyUploadAllowed` combination. `bypassKeyLock` skips
 * that per-key check entirely (BUSINESS_FINANCE keeps upload access past step 11).
 */
const CfV2Attachments = ({
  form,
  userDetailsMap = {},
  uploadingKey,
  canUpload = false,
  bypassKeyLock = false,
  editableFilesObject,
  fileInputRef,
  onFileChange,
  onReupload,
}) => (
  <Accordion
    defaultExpanded
    disableGutters
    elevation={0}
    sx={{ mt: 2, "&:before": { display: "none" } }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: "#008938", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
      >
      Commercial Feasibility
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0 }}>
      <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={onFileChange} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1,
        }}
      >
        {ATTACHMENT_FIELDS.map(({ key, label, accept }) => {
          const url = form?.attachments?.[key];
          const isUploading = uploadingKey === key;          
          // Per-key gate: the step/user must allow uploads AND this key must not be
          // locked (`false`) in the step's editableFilesObject — unless `bypassKeyLock`
          // (BUSINESS_FINANCE past step 11) overrides the per-step lock entirely.
          const userCanUpload = canUpload && (bypassKeyLock || isKeyUploadAllowed(editableFilesObject, key));
          const { Icon, color } = getFileIcon(accept);
          const meta = form?.attachmentsMeta?.[key];
          const uploadedAt = url
            ? meta?.uploadedAt || getAttachmentUploadedAt(form?.stepHistory, key, form?.createdAt)
            : null;
          const uploadedByName = meta?.uploadedBy
            ? (userDetailsMap[meta.uploadedBy] || meta.uploadedBy)
            : null;
          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minHeight: 64,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  backgroundColor: `${color}14`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color, fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                  noWrap
                  title={label}
                >
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  title={uploadedAt ? `Uploaded at ${moment(uploadedAt).format("DD MMM YYYY, hh:mm A")}${uploadedByName ? ` by ${uploadedByName}` : ""}` : ""}
                  noWrap
                >
                  {url
                    ? uploadedAt ? `${moment(uploadedAt).format("DD MMM YYYY, hh:mm A")}` : "N/A"
                    : "Not uploaded"}
                </Typography>
                {uploadedByName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    title={`Uploaded by ${uploadedByName}`}
                    noWrap
                  >
                    by {uploadedByName}
                  </Typography>
                )}
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="flex-end"
                sx={{ flexShrink: 0, minWidth: 72 }}
              >
                {url && (
                  <IconButton
                    size="small"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
                {userCanUpload && (
                  <IconButton
                    size="small"
                    onClick={() => onReupload(key)}
                    disabled={isUploading}
                    title={url ? "Re-upload" : "Upload"}
                  >
                 {isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon fontSize="small" />}
                  </IconButton>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </AccordionDetails>
  </Accordion>
);

export default CfV2Attachments;
